import React from "react";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "../ui/sheet";
import { SlidersHorizontal } from "@phosphor-icons/react";
import { money, pct } from "./fields";
import { DISCLOSURES } from "./YearTable";

const Row = ({ k, v }) => (
  <div className="flex justify-between gap-4 py-1.5 border-b border-exodus-border/50 text-xs"><span className="text-exodus-muted">{k}</span><span className="font-mono text-right">{v}</span></div>
);
const Group = ({ title, children }) => <div className="mb-6"><p className="eyebrow gold-text mb-2">{title}</p>{children}</div>;

const FORMULAS = [
  "Index credit per strategy = min(cap, max(floor, participation × avg index return − spread)), blended by allocation, then limited by the max illustrated rate.",
  "IUL each year: account value + premium − premium load − admin − per-$1,000 − rider − COI (net amount at risk × age-based rate × COI scale) + index credit.",
  "Death benefit = max(face, account value × IRS corridor factor). 7-pay limit ≈ face × age-based 7-pay rate. MEC is flagged if cumulative premiums exceed it in years 1-7.",
  "Policy loans accrue at the loan rate. 'Maximize' finds the highest level loan that keeps the policy in force to the illustrate-to age.",
  "FIA: account value × (1 + credited rate) − rider fee. Income base rolls up until income starts, then lifetime income = base × payout factor.",
  "Taxes: pre-tax distributions × (federal + state rate), plus 10% before 59½ (not for 457). Roth is tax-free if 59½+ and 5 years; otherwise earnings are taxed.",
];

export const AssumptionsDrawer = ({ inputs, result }) => {
  const c = inputs.client, p = inputs.iul, f = inputs.fia;
  return (
    <Sheet>
      <SheetTrigger asChild>
        <button type="button" data-testid="ro-assumptions-open"
          className="mb-6 lg:mb-0 lg:fixed lg:left-4 lg:bottom-4 z-40 bg-exodus-gold !text-exodus-bg px-4 py-3 text-[11px] uppercase tracking-[0.2em] inline-flex items-center gap-2 shadow-xl hover:brightness-110 transition-[filter]">
          <SlidersHorizontal size={14} /> Assumptions
        </button>
      </SheetTrigger>
      <SheetContent data-testid="ro-assumptions-drawer" className="bg-exodus-bg border-exodus-border text-exodus-text overflow-y-auto w-full sm:max-w-md">
        <SheetHeader><SheetTitle className="font-display text-2xl text-exodus-text text-left">Assumptions in use</SheetTitle></SheetHeader>
        <p className="text-xs text-exodus-muted mt-2 mb-6">Scenario: {inputs.scenario}. You can edit every value in the wizard. Defaults come from presets your advisor maintains.</p>
        <Group title="You & taxes">
          <Row k="Source / amount" v={`${c.source} · ${money(c.amount)}`} /><Row k="Age · income start · plan to" v={`${c.age} · ${c.income_start_age} · ${c.plan_to_age}`} />
          <Row k="Federal + state rate" v={`${pct(c.federal_rate, 1)} + ${pct(c.state_rate)}`} /><Row k="Direct rollover" v={c.direct_rollover ? "Yes" : "No"} />
          <Row k="Income" v={c.income_mode === "maximize" ? "Maximize" : `${money(c.income_amount)}/yr`} />
        </Group>
        <Group title="Keep in qualified account">
          <Row k="Return / fees" v={`${pct(inputs.qualified.annual_return, 1)} / ${pct(inputs.qualified.annual_fee)}`} />
        </Group>
        <Group title="IUL">
          {p.strategies.map((s, i) => <Row key={i} k={`${s.allocation}% ${s.name}`} v={`cap ${s.cap}% · par ${s.participation}% · spr ${s.spread}%`} />)}
          <Row k="Avg index return / max illustrated" v={`${pct(p.index_return)} / ${p.max_illustrated_rate ? pct(p.max_illustrated_rate) : "none"}`} />
          <Row k="Credited (blended)" v={result ? pct(result.iul.credited_rate) : "—"} />
          <Row k="Load · admin · per $1k" v={`${pct(p.premium_load, 1)} · $${p.admin_monthly}/mo · $${p.per_1000_annual}`} />
          <Row k="COI scale · rider" v={`${p.coi_multiplier}% · ${pct(p.rider_charge_pct)}`} />
          <Row k="Surrender" v={`${p.surrender_years} yrs from ${pct(p.surrender_first_pct, 1)}`} />
          <Row k="Loan" v={`${p.loan_type} @ ${pct(p.loan_rate)}`} />
        </Group>
        <Group title="FIA">
          <Row k="Crediting" v={f.crediting === "fixed" ? `fixed ${pct(f.fixed_rate)}` : `cap ${f.cap}% · par ${f.participation}% · idx ${f.index_return}%`} />
          <Row k="Surrender schedule" v={f.surrender_schedule.join("/")} />
          <Row k="Rider" v={f.rider ? `${f.rollup_rate}% ${f.rollup_type} × ${f.rollup_years}y · fee ${f.rider_fee}%` : "None"} />
        </Group>
        <Group title="How the math works">
          <ul className="space-y-2 text-[11px] text-exodus-muted leading-relaxed list-disc pl-4">{FORMULAS.map((x) => <li key={x}>{x}</li>)}</ul>
        </Group>
        <Group title="Disclosures">
          <ul className="space-y-1.5 text-[11px] text-exodus-muted leading-relaxed list-disc pl-4">{DISCLOSURES.map((x) => <li key={x}>{x}</li>)}</ul>
        </Group>
      </SheetContent>
    </Sheet>
  );
};
